import { FC, useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Stack, Chip, Avatar, Skeleton, Grid2 } from '@mui/material';
import { useParams } from 'react-router-dom';
import { LocationOn, Work, CalendarToday, Person, Assessment, MonetizationOn } from '@mui/icons-material';

import { useRooftopAdminFindOneQuery } from '@carcareers/store';
import { RooftopAdminFindOneDto } from '@libs/dto';
import {
  SkeletonForm,
  DashboardPageContainer,
  InfoCard,
  RooftopUpdateForm,
  RooftopInput,
  EmployerRooftopUpdateForm,
} from '@carcareers/components';
import { FormProvider } from '@carcareers/contexts';
import { useIsMobile } from '@carcareers/utils';

export const RooftopView: FC = () => {
  const id = useParams().id;
  const isMobile = useIsMobile();

  const [rooftop, setRooftop] = useState<RooftopAdminFindOneDto>();
  const { data, isLoading } = useRooftopAdminFindOneQuery(id ?? '0', { skip: !id });

  useEffect(() => {
    if (!isLoading && data) {
      setRooftop(data);
    }
  }, [isLoading, data]);

  const getInitials = (name?: string) => {
    return (
      name
        ?.split(' ')
        .map((word) => word.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2) || 'RT'
    );
  };

  const formatCurrency = (amount?: number) => {
    return amount ? `$${amount.toLocaleString()}` : '$0';
  };

  return (
    <DashboardPageContainer idReplacement={rooftop?.name}>
      <Stack spacing={3}>
        {/* Header Card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            backgroundColor: 'background.paper',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={3} alignItems={isMobile ? 'flex-start' : 'center'}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: 'primary.main',
                  fontSize: '1.5rem',
                  fontWeight: 600,
                }}
              >
                {isLoading ? '' : getInitials(rooftop?.name)}
              </Avatar>
              <Stack spacing={1} sx={{ flex: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {isLoading ? <Skeleton variant="text" width={200} height={32} /> : rooftop?.name || 'Unknown Rooftop'}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {isLoading ? (
                    <Skeleton variant="text" width={150} height={20} />
                  ) : (
                    rooftop?.location || 'Location not specified'
                  )}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                  <Chip icon={<LocationOn />} label={`ID: ${rooftop?.id || '...'}`} variant="outlined" sx={{ px: 1 }} />
                  <Chip
                    icon={<Person />}
                    label={`Owner: ${rooftop?.owner?.firstName || ''} ${rooftop?.owner?.lastName || ''}`}
                    variant="outlined"
                    sx={{ px: 1 }}
                  />
                  <Chip
                    icon={<CalendarToday />}
                    label={
                      rooftop?.createdAt ? `Created ${new Date(rooftop.createdAt).toLocaleDateString()}` : 'Created ...'
                    }
                    variant="outlined"
                    sx={{ px: 1 }}
                  />
                  {rooftop?.isArchived && <Chip label="Archived" color="warning" variant="filled" sx={{ px: 1 }} />}
                </Stack>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
        <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
          <Card
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 3,
              backgroundColor: 'background.paper',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <Work sx={{ color: 'primary.contrastText', fontSize: 28 }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                {isLoading ? <Skeleton variant="text" width={40} height={32} /> : rooftop?.activeJobs || 0}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Active Jobs
              </Typography>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 3,
              backgroundColor: 'background.paper',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: 'success.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <Assessment sx={{ color: 'success.contrastText', fontSize: 28 }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main', mb: 1 }}>
                {isLoading ? <Skeleton variant="text" width={40} height={32} /> : rooftop?.totalApplications || 0}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Total Applications
              </Typography>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 3,
              backgroundColor: 'background.paper',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: 'info.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <MonetizationOn sx={{ color: 'primary.contrastText', fontSize: 28 }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'info.main', mb: 1 }}>
                {isLoading ? <Skeleton variant="text" width={60} height={32} /> : formatCurrency(rooftop?.totalRevenue)}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Total Revenue
              </Typography>
            </CardContent>
          </Card>
        </Stack>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <InfoCard label="Total Jobs" value={rooftop?.totalJobs || 0} loading={isLoading} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <InfoCard label="Inactive Jobs" value={rooftop?.inactiveJobs || 0} loading={isLoading} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <InfoCard label="Total Posts" value={rooftop?.totalPosts || 0} loading={isLoading} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <InfoCard label="Active Posts" value={rooftop?.activePosts || 0} loading={isLoading} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <InfoCard label="Recent Applications" value={rooftop?.recentApplications || 0} loading={isLoading} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <InfoCard label="Avg Apps/Job" value={rooftop?.averageApplicationsPerJob || 0} loading={isLoading} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <InfoCard label="Authorized Employers" value={rooftop?.authorizedEmployersCount || 0} loading={isLoading} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <InfoCard
              label="Pending Authorizations"
              value={rooftop?.pendingAuthorizationsCount || 0}
              loading={isLoading}
            />
          </Grid2>
        </Grid2>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <InfoCard label="Contact Email" value={rooftop?.email || 'Not specified'} loading={isLoading} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <InfoCard label="Phone Number" value={rooftop?.phone || 'Not specified'} loading={isLoading} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <InfoCard label="Website" value={rooftop?.website || 'Not specified'} loading={isLoading} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <InfoCard
              label="Coordinates"
              value={rooftop?.latitude && rooftop?.longitude ? `${rooftop.latitude}, ${rooftop.longitude}` : 'Not set'}
              loading={isLoading}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <InfoCard
              label="Last Job Posted"
              value={rooftop?.lastJobPosted ? new Date(rooftop.lastJobPosted).toLocaleDateString() : 'Never'}
              loading={isLoading}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <InfoCard
              label="Last Application"
              value={
                rooftop?.lastApplicationReceived
                  ? new Date(rooftop.lastApplicationReceived).toLocaleDateString()
                  : 'Never'
              }
              loading={isLoading}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <InfoCard label="Most Popular Job" value={rooftop?.mostPopularJobTitle || 'None'} loading={isLoading} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <InfoCard label="Posts This Month" value={rooftop?.activePostsThisMonth || 0} loading={isLoading} />
          </Grid2>
        </Grid2>
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            backgroundColor: 'background.paper',
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOn sx={{ color: 'primary.main' }} />
                Rooftop Details & Management
              </Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <FormProvider initialData={rooftop}>
                <EmployerRooftopUpdateForm />
              </FormProvider>
            </Box>
          </CardContent>
        </Card>
      </Stack>
    </DashboardPageContainer>
  );
};

export default RooftopView;
